import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';

import { of, catchError, map } from 'rxjs';
import { isNumber } from 'underscore';

import { UserPreferencesService } from '../../../../../Account/UserPreferences/services/user-preferences.service';
import { userAppPreferencesDefault } from '../../../../../Core/Users/data/user-app-preferences-default';
import { UserPreferenceChartColor } from '../../../../../Core/Users/models/user-app-preferences-chart-colors.model';
import { ChartDisplayElementModel } from '../../../../Reports/models/chart-display-element.model';
import { ChartColorPickerFieldModel } from '../../../models/chart-color-picker-field.model';
import { MapChartColorSchemeEnum } from '../../../../Charts/models/map-chart.model';
import { ReportBlockTypeEnum } from '../../../../Reports/enums/report-block-type.enum';
import { MentionMetadataTypesEnum } from '../../../../../Monitor/Shared/enums/mention-metadata-types.enum';
import { ColorPickerFieldComponent } from '../ColorPickerField/color-picker-field.component';

@Component({
    selector: 'agpr-chart-color-picker-field',
    templateUrl: '../ColorPickerField/color-picker-field.component.html',
    styleUrls: ['../ColorPickerField/color-picker-field.component.scss', './chart-color-picker-filed.component.scss'],
    standalone: false
})
export class ChartColorPickerFieldComponent extends ColorPickerFieldComponent implements OnInit, OnDestroy, OnChanges {
    @Input() field: ChartColorPickerFieldModel;
    @Input() userPreferenceColor: UserPreferenceChartColor = {
        colorType: 'barColors',
        typeProperty: 'barColor'
    };

    constructor(private userPreferencesService: UserPreferencesService) {
        super();
    }

    ngOnInit(): void {
        if (this.field?.userPreferenceColor) {
            this.userPreferenceColor = this.field.userPreferenceColor;
        }
        this.subscriptions.push(
            this.userPreferencesService.getUserAppPreferences().pipe(
                map(userAppPrefs => userAppPrefs.reportsPrefs.defaultChartColors[this.userPreferenceColor.colorType]),
                catchError(() => of(userAppPreferencesDefault.reportsPrefs.defaultChartColors[this.userPreferenceColor.colorType]))
            ).subscribe(colors => {
                if (!this.value) {
                    if (this.block?.displayElement?.styles?.colorMap[this.field.key]) {
                        this.setValue(this.block.displayElement.styles.colorMap[this.field.key]);
                    } else {
                        this.setValue(colors[this.userPreferenceColor.typeProperty]);
                    }
                }
            })
        );
    }

    /**
     * The only purpose for this method  is to hide and show the color picker based on the current metrics.
     */
    ngOnChanges(): void {
        switch (this.block.type.key) {
            case ReportBlockTypeEnum.COVERAGE_OVER_TIME:
                this.togglePickerByMetrics();
                break;
            case ReportBlockTypeEnum.GEOGRAPHY_BREAKDOWN_BY_VOLUME:
                this.togglePickerByMapChartColorScheme();
                break;
            case ReportBlockTypeEnum.SHARE_OF_VOICE:
            case ReportBlockTypeEnum.COVERAGE_COMPARISON:
                this.togglePickerBySOVParentSourceType();
                break;
            case ReportBlockTypeEnum.TOP_MENTIONS:
                this.togglePickerByTopMentionsMetadataType();
                break;
        }
        if (this.field.appearCondition) {
            this.showPicker = this.field.appearCondition(this.block.displayElement);
        }
    }

    togglePickerByMetrics(): void {
        // multi color pickers are only used for multi metrics
        const metrics = (this.block.displayElement as ChartDisplayElementModel).metrics;
        if (!metrics){
            return;
        }
        // if there's multiple picker the label of the picker will be something like 'Analysis - 4'
        const potentialPickerNumber = +this.field.label.substring(this.field.label.length - 1);
        if (isNumber(potentialPickerNumber) && potentialPickerNumber > metrics.length) {
            this.showPicker = false;
        } else {
            this.showPicker = true;
        }
    }

    togglePickerBySOVParentSourceType(): void {
        const series = (this.block.displayElement as ChartDisplayElementModel).series;
        if (!series || !series.length) {
            return;
        }
        const match = this.field.key.match(/\d+$/);
        const index = match ? Number(match[0]) : NaN;
        if (
            series.every(s => s.styles?.parentGroupId || s.styles?.parentThemeId)
            && (!isNaN(index) && index <= series.length)
        ) {
            this.showPicker = true;
            this.field.label = `Reports.formField.metadata.${series[index - 1].type}${index}`;
        } else {
            this.showPicker = false;
        }
    }

    togglePickerByMapChartColorScheme(): void {
        const mapChartColorScheme = (this.block.displayElement as ChartDisplayElementModel).mapChartColorScheme;
        if (!mapChartColorScheme) {
            return;
        }
        const potentialPickerNumber = +this.field.label.substring(this.field.label.length - 1);
        this.showPicker = (mapChartColorScheme === MapChartColorSchemeEnum.CHOROPLETH && isNaN(potentialPickerNumber))
            || (mapChartColorScheme === MapChartColorSchemeEnum.REGION_COLORS && !isNaN(potentialPickerNumber));
    }

    togglePickerByTopMentionsMetadataType(): void {
        const metadataTypeForDisplay = [MentionMetadataTypesEnum.audienceReach, MentionMetadataTypesEnum.ave, MentionMetadataTypesEnum.domainAuthority];
        const metadataTypes = (this.block.displayElement as ChartDisplayElementModel).topMentionsMetadata?.metadataTypes.filter(
            x => metadataTypeForDisplay.includes(x)
        );
        if (!metadataTypes?.length) {
            this.showPicker = false;
            return;
        }
        const index = +this.field.key.substring(this.field.key.length - 1);
        if (!isNaN(index) && index <= metadataTypes.length) {
            this.showPicker = true;
            this.field.label = `Reports.topMentions.metadata.${metadataTypes[index - 1]}`;
        } else {
            this.showPicker = false;
        }
    }

    ngOnDestroy(): void {
        this.subscriptions?.forEach(sub => sub.unsubscribe());
    }
}
